import { makeAutoObservable, runInAction } from "mobx";
import { db } from "@stores/environments/Database.web";
import { ref, get, set, push, child } from "firebase/database";
import { IContent } from "@interfaces/icontent";
import { logService } from "../services/LogService";
import { authStore } from "./AuthStore";

class ExtraStore {
  extras: IContent[] = [];
  loading: boolean = false;
  error: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchExtras = async () => {
    this.loading = true;
    const dbRef = ref(db);

    try {
      const snapshot = await get(child(dbRef, "extras"));
      if (snapshot.exists()) {
        const data = snapshot.val();
        const allExtras: IContent[] = Object.keys(data).map((key) => ({
          id: key,
          ...data[key],
        }));
        runInAction(() => {
          this.extras = allExtras
            .filter(extra => !extra.deleted)
            .sort((a, b) => (a.title || "").localeCompare(b.title || ""));
          this.loading = false;
        });
      } else {
        runInAction(() => {
          this.extras = [];
          this.loading = false;
        });
      }
    } catch (error) {
      console.error("Erro ao buscar extras:", error);
      runInAction(() => {
        this.error = "Erro ao carregar extras.";
        this.loading = false;
      });
    }
  };

  saveExtra = async (extra: Omit<IContent, "id">, id?: string) => {
    const extraId = id || push(ref(db, "extras")).key;
    const dbRef = ref(db, `extras/${extraId}`);
    
    // Verifica se é uma criação ou atualização
    const isUpdate = !!id;
    
    // Busca o valor anterior se for uma atualização
    let oldValue = null;
    if (isUpdate) {
      try {
        const snapshot = await get(dbRef);
        oldValue = snapshot.exists() ? snapshot.val() : null;
      } catch (error) {
        console.error('Erro ao buscar valor anterior:', error);
      }
    }
    
    await set(dbRef, extra);
    
    // Registra o log
    if (authStore._user) {
      const logData = isUpdate 
        ? logService.updateLogData(
            authStore._user.uid,
            authStore._user.email || '',
            'Extra',
            extraId,
            `extras/${extraId}`,
            oldValue,
            extra,
            `Extra ${isUpdate ? 'atualizado' : 'criado'}: ${extra.title}`
          )
        : logService.createLogData(
            authStore._user.uid,
            authStore._user.email || '',
            'Extra',
            extraId,
            `extras/${extraId}`,
            extra,
            `Extra criado: ${extra.title}`
          );
      
      await logService.logOperation(logData);
    }
    
    this.fetchExtras();
  };

  deleteExtra = async (id: string) => {
    const confirmed = window.confirm("Tem certeza que deseja excluir este extra?");
    if (!confirmed) return;

    // Busca o valor anterior para o log
    const dbRef = ref(db, `extras/${id}`);
    let oldValue = null;
    try {
      const snapshot = await get(dbRef);
      oldValue = snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      console.error('Erro ao buscar valor anterior:', error);
    }

    const deleteRef = ref(db, `extras/${id}/deleted`);
    await set(deleteRef, true);
    
    // Registra o log
    if (authStore._user && oldValue) {
      const logData = logService.deleteLogData(
        authStore._user.uid,
        authStore._user.email || '',
        'Extra',
        id,
        `extras/${id}`,
        oldValue,
        `Extra excluído: ${oldValue.title}`
      );
      
      await logService.logOperation(logData);
    }
    
    this.fetchExtras();
  };
}

export const extraStore = new ExtraStore();
